import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type {
  BorderRadiusProps,
  BottomProps,
  DisplayProps,
  PositionProps,
  RightProps,
} from 'styled-system';
import {
  background,
  backgroundColor,
  borderRadius,
  bottom,
  display,
  position,
  right,
  themeGet,
} from 'styled-system';

import {
  flexShrink,
  themeBottom,
  themeLeft,
  transform,
  transition,
} from 'common/theme/system-utilities';
import type {
  FlexShrinkProps,
  ThemeBottomProps,
  ThemeLeftProps,
  TransformProps,
  TransitionProps,
} from 'common/theme/system-utilities';
import type { GalleryTrackingProps } from 'common/tracking/productGallery';
import { Flex } from '../Flex';
import GalleryTabMenu from '../GalleryTabMenu';
import type { GalleryTabMenuTab } from '../GalleryTabMenu';

const MAX_VISIBLE_DOTS = 5;
const DOT_SIZE_DEFAULT = 8;
const DOT_SIZE_EDGE = 4;

const getVisibleStart = (currentSlide: number, slideCount: number): number =>
  Math.min(
    Math.max(0, currentSlide - Math.floor(MAX_VISIBLE_DOTS / 2)),
    Math.max(0, slideCount - MAX_VISIBLE_DOTS)
  );

type DotSizeProps = {
  index: number;
  visibleCount: number;
  hasMoreBefore: boolean;
  hasMoreAfter: boolean;
};

const getDotSize = ({ index, visibleCount, hasMoreBefore, hasMoreAfter }: DotSizeProps): number => {
  if (index === 0 && hasMoreBefore) return DOT_SIZE_EDGE;
  if (index === visibleCount - 1 && hasMoreAfter) return DOT_SIZE_EDGE;

  return DOT_SIZE_DEFAULT;
};

type DotStyleProps = DisplayProps & BorderRadiusProps & FlexShrinkProps & TransitionProps;

const Dot = styled.span<DotStyleProps & { size: number; isActive: boolean }>`
  ${display};
  ${borderRadius};
  ${flexShrink};
  ${background};
  ${transition};

  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  background-color: ${({ isActive }) =>
    isActive
      ? themeGet('colors.sys.neutral.icon.active')
      : themeGet('colors.sys.neutral.icon.inactive')};
`;

const dotStyles: DotStyleProps = {
  display: 'block',
  borderRadius: 'circle',
  flexShrink: 'auto',
  transition: 'width 0.2s ease, height 0.2s ease, background-color 0.2s ease',
};

const StyledContainer = styled(Flex)<ThemeBottomProps & ThemeLeftProps & TransformProps>`
  ${themeBottom};
  ${themeLeft};
  ${transform};
  ${backgroundColor};
`;

const containerStyles: ComponentProps<typeof StyledContainer> = {
  position: 'absolute',
  themeLeft: '50%',
  transform: 'translateX(-50%)',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 'sp_8',
  zIndex: 1,
  px: 'sp_12',
  width: '100%',
  boxSizing: 'border-box',
};

const StyledTabContainer = styled(Flex)<PositionProps & RightProps & BottomProps>`
  ${position};
  ${right};
  ${bottom};
`;

const styledTabContainerStyles: ComponentProps<typeof StyledTabContainer> = {
  position: 'absolute',
  bottom: -8,
  right: 16,
};

const pillStyles: ComponentProps<typeof Flex> = {
  alignItems: 'center',
  gap: 'sp_8',
  backgroundColor: 'sys.neutral.background.default',
  px: 'sp_8',
  py: 'sp_4',
  borderRadius: 'x-large',
};

interface PaginationWithTabMenuProps {
  currentSlide: number;
  slideCount: number;
  tabs: GalleryTabMenuTab[];
  activeTabId?: string;
  onTabSelect: (id: string) => void;
  model3DData?: Nullable<{ path: string }>;
  tracking?: GalleryTrackingProps;
}

const PaginationWithTabMenu = ({
  currentSlide,
  slideCount,
  tabs,
  activeTabId,
  onTabSelect,
  model3DData,
  tracking,
}: PaginationWithTabMenuProps) => {
  const visibleCount = Math.min(slideCount, MAX_VISIBLE_DOTS);
  const visibleStart = getVisibleStart(currentSlide, slideCount);
  const hasMoreBefore = visibleStart > 0;
  const hasMoreAfter = visibleStart + visibleCount < slideCount;
  const only3DButton = Boolean(model3DData) && tabs.length === 1;

  const tabMenu = (
    <GalleryTabMenu
      tabs={tabs}
      activeTabId={activeTabId}
      onTabSelect={onTabSelect}
      model3DData={model3DData}
      tracking={tracking}
    />
  );

  return (
    <StyledContainer
      {...containerStyles}
      themeBottom={only3DButton ? 'sp_16' : ['sp_12', null, null, 'sp_16']}
    >
      {slideCount > 1 && (
        <Flex {...pillStyles}>
          {Array.from({ length: visibleCount }, (_, index) => {
            const slideIndex = visibleStart + index;

            return (
              <Dot
                {...dotStyles}
                key={slideIndex}
                size={getDotSize({ index, visibleCount, hasMoreBefore, hasMoreAfter })}
                isActive={slideIndex === currentSlide}
              />
            );
          })}
        </Flex>
      )}
      {only3DButton ? (
        <StyledTabContainer {...styledTabContainerStyles}>{tabMenu}</StyledTabContainer>
      ) : (
        tabMenu
      )}
    </StyledContainer>
  );
};

export default PaginationWithTabMenu;
